import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  TrendingUp, Zap, Award, Crown, Crosshair,
  RefreshCw, FlaskConical, ChevronRight, BookOpen,
  Flame, Lightbulb, History, BarChart2, ArrowRight,
  Sparkles, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  growth: {
    icon: TrendingUp,
    color: '#3dffa0',
    accent: 'rgba(61,255,160,0.1)',
    border: 'rgba(61,255,160,0.25)',
    label: 'Growth',
    desc: 'Scaling, acquisition & virality',
  },
  conversion: {
    icon: Zap,
    color: '#f97316',
    accent: 'rgba(249,115,22,0.1)',
    border: 'rgba(249,115,22,0.25)',
    label: 'Conversion',
    desc: 'Funnels, deals & optimization',
  },
  credibility: {
    icon: Award,
    color: '#60a5fa',
    accent: 'rgba(96,165,250,0.1)',
    border: 'rgba(96,165,250,0.25)',
    label: 'Credibility',
    desc: 'Trust, authority & social proof',
  },
  leadership: {
    icon: Crown,
    color: '#a68eff',
    accent: 'rgba(166,142,255,0.1)',
    border: 'rgba(166,142,255,0.25)',
    label: 'Leadership',
    desc: 'Vision, culture & decision-making',
  },
  strategy: {
    icon: Crosshair,
    color: '#f0c27f',
    accent: 'rgba(240,194,127,0.1)',
    border: 'rgba(240,194,127,0.25)',
    label: 'Strategy & Tactics',
    desc: 'Positioning, frameworks & execution',
  },
};

const SCORE_COLOR = s => s >= 85 ? '#3dffa0' : s >= 70 ? '#f0c27f' : '#fb923c';
const URGENCY_COLOR = { high: '#ff5370', medium: '#f0c27f', low: '#3dffa0' };

function TopicCard({ topic, catColor, onResearch, onBrainstorm }) {
  return (
    <div
      className="rounded-xl p-4 flex flex-col gap-3 transition-all hover:scale-[1.01]"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--llmc-border)' }}
      data-testid="trending-topic-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white leading-snug mb-1">{topic.title}</div>
          <div className="text-[11px] leading-relaxed" style={{ color: 'var(--llmc-text-muted)' }}>
            {topic.angle}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <div className="text-base font-black" style={{ color: SCORE_COLOR(topic.score || 70) }}>
            {topic.score || 70}
          </div>
          <div className="text-[9px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: `${URGENCY_COLOR[topic.urgency || 'medium']}15`, color: URGENCY_COLOR[topic.urgency || 'medium'] }}>
            {(topic.urgency || 'MEDIUM').toUpperCase()}
          </div>
        </div>
      </div>

      {topic.why_hot && (
        <div className="flex items-start gap-2 rounded-lg px-2.5 py-2"
          style={{ background: `${catColor}0d`, border: `1px solid ${catColor}20` }}>
          <Flame size={10} style={{ color: catColor, flexShrink: 0, marginTop: 1 }} />
          <span className="text-[10px] leading-relaxed" style={{ color: `${catColor}cc` }}>
            {topic.why_hot}
          </span>
        </div>
      )}

      <div className="flex gap-2 pt-1" style={{ borderTop: '1px solid var(--llmc-border)' }}>
        <button
          onClick={() => onResearch(topic.title)}
          data-testid="topic-research-btn"
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(61,255,160,0.08)', border: '1px solid rgba(61,255,160,0.2)', color: '#3dffa0' }}
        >
          <FlaskConical size={10} /> Research
        </button>
        <button
          onClick={() => onBrainstorm(topic.title)}
          data-testid="topic-brainstorm-btn"
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(124,94,250,0.08)', border: '1px solid rgba(124,94,250,0.2)', color: '#a68eff' }}
        >
          <Lightbulb size={10} /> Brainstorm
        </button>
      </div>
    </div>
  );
}

function CategoryPanel({ catKey, catData, isActive, onSelect }) {
  const meta = CATEGORY_META[catKey] || {};
  const Icon = meta.icon || TrendingUp;
  const topicCount = catData?.topics?.length || 0;

  return (
    <button
      onClick={() => onSelect(catKey)}
      data-testid={`category-tab-${catKey}`}
      className="flex flex-col items-center gap-1.5 px-4 py-3 rounded-2xl transition-all"
      style={{
        background: isActive ? meta.accent : 'rgba(255,255,255,0.02)',
        border: `1.5px solid ${isActive ? meta.border : 'var(--llmc-border)'}`,
        flex: 1,
        boxShadow: isActive ? `0 0 20px ${meta.color}15` : 'none',
      }}
    >
      <div
        className="w-8 h-8 rounded-xl flex items-center justify-center"
        style={{ background: isActive ? `${meta.color}20` : 'rgba(255,255,255,0.04)', border: `1px solid ${meta.color}30` }}
      >
        <Icon size={15} style={{ color: meta.color }} />
      </div>
      <div className="text-[11px] font-bold text-white">{meta.label}</div>
      {topicCount > 0 && (
        <div className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
          style={{ background: `${meta.color}15`, color: meta.color }}>
          {topicCount} topics
        </div>
      )}
    </button>
  );
}

export default function TrendingTopics() {
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('growth');
  const [cachedAt, setCachedAt] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const loadCategories = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/trends/categories`, {
        params: { refresh: forceRefresh },
      });
      setCategories(data.categories || {});
      setCachedAt(data.cached_at || data.generated_at);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load trending topics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleResearch = (topic) => {
    navigate('/research', { state: { topic, fromTrending: true } });
  };

  const handleBrainstorm = (topic) => {
    navigate('/brainstorm', { state: { idea: topic } });
  };

  const activeCat = categories[activeCategory] || {};
  const activeMeta = CATEGORY_META[activeCategory] || {};
  const ActiveIcon = activeMeta.icon || TrendingUp;

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4" style={{ borderBottom: '1px solid var(--llmc-border)', background: 'rgba(10,10,20,0.95)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #f97316, #f0c27f)' }}>
              <Flame size={20} className="text-white" />
            </div>
            <div>
              <div className="font-black text-white text-sm" style={{ letterSpacing: '-0.01em' }}>Trending Topics</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
                AI-curated by category: Growth · Conversion · Credibility · Leadership · Strategy
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {cachedAt && (
              <div className="flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>
                <Clock size={10} />
                {new Date(cachedAt).toLocaleString()}
              </div>
            )}
            <button
              onClick={() => loadCategories(true)}
              disabled={loading}
              data-testid="trending-refresh-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--llmc-border)', color: 'var(--llmc-text-muted)' }}
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Generating...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {error && (
          <div className="max-w-3xl mx-auto rounded-xl p-4 text-sm"
            style={{ background: 'rgba(255,83,112,0.08)', border: '1px solid rgba(255,83,112,0.3)', color: '#ff7a94' }}>
            {error}
          </div>
        )}

        {loading && Object.keys(categories).length === 0 && (
          <div className="max-w-3xl mx-auto text-center py-16 space-y-4">
            <div className="w-12 h-12 mx-auto rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #f97316, #f0c27f)' }}>
              <Sparkles size={24} className="text-white animate-pulse" />
            </div>
            <div className="text-white font-bold text-lg">Einstein is analyzing trends...</div>
            <div className="text-sm" style={{ color: 'var(--llmc-text-muted)' }}>
              Generating AI-powered topics for all 5 categories. This takes ~20 seconds.
            </div>
          </div>
        )}

        {Object.keys(categories).length > 0 && (
          <div className="max-w-5xl mx-auto space-y-5" style={{ animation: 'fade-in 0.4s ease' }}>
            {/* Category Tabs */}
            <div className="flex gap-2.5">
              {Object.keys(CATEGORY_META).map(key => (
                <CategoryPanel
                  key={key}
                  catKey={key}
                  catData={categories[key]}
                  isActive={activeCategory === key}
                  onSelect={setActiveCategory}
                />
              ))}
            </div>

            {/* Active Category Content */}
            {activeCat && (
              <div className="space-y-5">
                {/* Category Header */}
                <div className="flex items-center gap-4 rounded-2xl p-5"
                  style={{ background: activeMeta.accent, border: `1px solid ${activeMeta.border}` }}>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${activeMeta.color}20`, border: `1px solid ${activeMeta.color}40` }}>
                    <ActiveIcon size={22} style={{ color: activeMeta.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-lg font-black text-white mb-0.5">{activeMeta.label}</div>
                    <div className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>{activeMeta.desc}</div>
                  </div>
                  <div className="text-[10px] font-bold px-3 py-1.5 rounded-full flex-shrink-0"
                    style={{ background: `${activeMeta.color}15`, color: activeMeta.color, border: `1px solid ${activeMeta.color}30` }}>
                    {(activeCat.topics || []).length} topics
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {/* Topics Column (2/3 width) */}
                  <div className="col-span-2 space-y-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest mb-2"
                      style={{ color: 'var(--llmc-text-muted)' }}>Trending Topics</div>
                    {(activeCat.topics || []).length === 0 ? (
                      <div className="text-center py-8 rounded-xl text-sm"
                        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)', color: 'var(--llmc-text-muted)' }}>
                        No topics generated yet. Click Refresh to generate.
                      </div>
                    ) : (
                      activeCat.topics.map((topic, i) => (
                        <TopicCard
                          key={i}
                          topic={topic}
                          catColor={activeMeta.color}
                          onResearch={handleResearch}
                          onBrainstorm={handleBrainstorm}
                        />
                      ))
                    )}
                  </div>

                  {/* Insights Column (1/3 width) */}
                  <div className="col-span-1 space-y-4">
                    {/* Strategic Insight */}
                    {activeCat.strategic_insight && (
                      <div className="rounded-2xl p-4 space-y-2"
                        style={{ background: `${activeMeta.color}08`, border: `1px solid ${activeMeta.border}` }}>
                        <div className="flex items-center gap-2">
                          <BarChart2 size={12} style={{ color: activeMeta.color }} />
                          <span className="text-[10px] font-bold uppercase tracking-wider"
                            style={{ color: activeMeta.color }}>Strategic Insight</span>
                        </div>
                        <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
                          {activeCat.strategic_insight}
                        </p>
                      </div>
                    )}

                    {/* Historical Lesson */}
                    {activeCat.historical_lesson && (
                      <div className="rounded-2xl p-4 space-y-2"
                        style={{ background: 'rgba(240,194,127,0.06)', border: '1px solid rgba(240,194,127,0.2)' }}>
                        <div className="flex items-center gap-2">
                          <History size={12} style={{ color: '#f0c27f' }} />
                          <span className="text-[10px] font-bold uppercase tracking-wider"
                            style={{ color: '#f0c27f' }}>Historical Lesson</span>
                        </div>
                        <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
                          {activeCat.historical_lesson}
                        </p>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="rounded-2xl p-4 space-y-2"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
                      <div className="text-[10px] font-bold uppercase tracking-wider mb-2"
                        style={{ color: 'var(--llmc-text-muted)' }}>Quick Actions</div>
                      <button
                        onClick={() => navigate('/research')}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                        style={{ background: 'rgba(61,255,160,0.06)', border: '1px solid rgba(61,255,160,0.2)', color: '#3dffa0' }}
                      >
                        <FlaskConical size={12} /> Open Research Lab
                        <ArrowRight size={10} className="ml-auto" />
                      </button>
                      <button
                        onClick={() => navigate('/brainstorm')}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                        style={{ background: 'rgba(124,94,250,0.06)', border: '1px solid rgba(124,94,250,0.2)', color: '#a68eff' }}
                      >
                        <Lightbulb size={12} /> Open Brainstorm Lab
                        <ArrowRight size={10} className="ml-auto" />
                      </button>
                      <button
                        onClick={() => navigate('/blog')}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                        style={{ background: 'rgba(0,212,184,0.06)', border: '1px solid rgba(0,212,184,0.2)', color: '#00d4b8' }}
                      >
                        <BookOpen size={12} /> Write a Blog Post
                        <ArrowRight size={10} className="ml-auto" />
                      </button>
                    </div>

                    {/* All Category Overview (mini) */}
                    <div className="rounded-2xl p-4 space-y-2"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
                      <div className="text-[10px] font-bold uppercase tracking-wider mb-3"
                        style={{ color: 'var(--llmc-text-muted)' }}>All Categories</div>
                      {Object.entries(CATEGORY_META).map(([key, meta]) => {
                        const cat = categories[key] || {};
                        const CatIcon = meta.icon;
                        return (
                          <button
                            key={key}
                            onClick={() => setActiveCategory(key)}
                            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all hover:opacity-80"
                            style={{
                              background: activeCategory === key ? meta.accent : 'transparent',
                              border: `1px solid ${activeCategory === key ? meta.border : 'transparent'}`,
                            }}
                          >
                            <CatIcon size={12} style={{ color: meta.color, flexShrink: 0 }} />
                            <span className="text-[11px] font-medium flex-1 text-left text-white">{meta.label}</span>
                            <span className="text-[10px]" style={{ color: meta.color }}>
                              {(cat.topics || []).length}
                            </span>
                            <ChevronRight size={10} style={{ color: 'var(--llmc-text-muted)' }} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
